<!--Copyright (c) PikPok. All rights reserved-->
<template>
  <v-container :fluid="true" class="ma-0 pa-0" v-if="loading">
    <v-row class="text-white bg-yellow-darken-3 pa-3 mt-0">
      <router-link to="/">
        <v-icon color="white" icon="mdi-less-than" size="x-large" class="pr-5 pl-5"></v-icon>
      </router-link>
      <v-icon color="white" icon="mdi-account" size="x-large" class="pr-5 pl-5"></v-icon>
      <h3 class="pt-1">
        {{ bugReport.strings.BUG_ACCOUNT_AND_DATA }}
      </h3>
    </v-row>
    <v-row v-if="!formSubmit">
      <v-col offset-lg="2" lg="8" offset="1" cols="10">
        <v-select
            v-model="formDetails.lost_items_and_account_recovery"
            :items="bugReport.lost_items_and_account_recovery"
            :rules="[v => !!v]"
            :label=bugReport.strings.BUG_ACCOUNT_AND_DATA
            :placeholder=bugReport.strings.BUG_CHOOSE_OPTION
            required variant="solo-filled" return-object
        ></v-select>
        <v-form ref="form" lazy-validation v-if="!!formDetails.lost_items_and_account_recovery">
          <v-text-field
              v-model="formDetails.original_player_id"
              :rules="[v => !!v || !!formDetails.new_playeri_d || !!formDetails.in_game_username || 'Please provide either Player ID or In-Game Username']"
              :label=bugReport.strings.BUG_ORIGINAL_PLAYER_ID
              required variant="solo-filled"
          ></v-text-field>
          <v-text-field
              v-model="formDetails.new_player_id"
              :rules="[v => !!v || !!formDetails.original_player_id || !!formDetails.in_game_username]"
              :label=bugReport.strings.BUG_NEW_PLAYER_ID
              required variant="solo-filled"
          ></v-text-field>
          <!-- How to find player ID -->
          <v-expansion-panels class="mb-5">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-subtitle-1 bg-amber-lighten-4">
                {{ bugReport.strings.PRIVACY_HOW_FIND }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                Your <b>PikPokPlus ID</b> is a 9-digit number displayed in the game. Here's how you can find it:
                <ol class="ml-7 mt-5">
                  <li>
                    <h3>From the Main Menu</h3>
                    <ul class="ml-5 mt-2">
                      <li>Look at the <b>bottom left corner</b> of most menu screens.</li>
                      <li>The 9-digit <b>PikPokPlus ID</b> should appear below the <b>Settings icon.</b></li>
                    </ul>
                  </li>
                  <li class="mt-3">
                    <h3>From the Settings Menu</h3>
                    <ul class="ml-5 mt-2">
                      <li>Tap the <b>Settings icon</b> in the bottom left corner of the screen.</li>
                      <li>Open the <b>Settings menu.</b></li>
                      <li>Your <b>PikPokPlus ID</b> will appear in the <b>bottom right corner</b> of the Settings menu.
                      </li>
                    </ul>
                  </li>
                </ol>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-text-field
              v-model="formDetails.in_game_username"
              :label=bugReport.strings.BUG_IN_GAME_USERNAME
              :placeholder=bugReport.strings.BUG_IN_GAME_USERNAME_HINT
              required
              variant="solo-filled"
              @blur="validateUsername"
          ></v-text-field>
          <v-text-field
              v-model="formDetails.email"
              :rules="[v => !!v, v => !v || /\s*\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})\s*/.test(v)]"
              :label=bugReport.strings.BUG_EMAIL
              :placeholder=bugReport.strings.BUG_EMAIL_HINT
              required variant="solo-filled"
          ></v-text-field>
          <v-select
              v-model="formDetails.game"
              :items="appList"
              :rules="[v => !!v]"
              :label=bugReport.strings.BUG_GAME
              :placeholder=bugReport.strings.BUG_CHOOSE_OPTION
              required variant="solo-filled"
          ></v-select>
          <v-text-field
              v-model="formDetails.order_id"
              :label=bugReport.strings.BUG_ANDROID_USER_ORDER_ID
              :placeholder=bugReport.strings.BUG_ANDROID_USER_ORDER_ID_INFO
              required variant="solo-filled"
          ></v-text-field>
          <!-- How to find Order ID -->
          <v-expansion-panels class="mb-5">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-subtitle-1 bg-amber-lighten-4">
                {{ bugReport.strings.PRIVACY_HOW_FIND }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <h3>If You Have an Android Device:</h3>
                <ul class="ml-7 mt-5">
                  <li>
                    Provide either:
                    <ul class="ml-5 mt-2">
                      <li>The <b>email address</b> associated with your <b>Google Play account</b>, or</li>
                      <li>A <b>screenshot</b> of your order confirmation receipt.</li>
                    </ul>
                  </li>
                </ul>
                <p class="ma-3">The order confirmation receipt must display the following details:</p>
                <ul class="ml-10">
                  <li><b>Item Name</b></li>
                  <li><b>Order Date</b></li>
                  <li><b>Order Number</b> (This starts with "GPA" followed by a long string of numbers, e.g.,
                    GPA.1234-1234-1234-12345).
                  </li>
                </ul>

                <h3 class="mt-5">If You Made the Purchase on an Apple Device:</h3>
                <ul class="mt-3 ml-7">
                  <li>
                    Follow the directions in this Apple support article to locate your receipt details:
                    <a href="https://support.apple.com/en-is/HT204088" target="_blank"
                       style="text-decoration: underline">Apple Support: View Purchase History.</a>
                  </li>
                </ul>

                <h3 class="mt-5">Important Notes:</h3>
                <ul class="mt-3 ml-7">
                  <li><b>Receipts Required:</b> We can only track purchases using the official <b>confirmation
                    receipts.</b></li>
                  <li><b>Unsupported Proofs:</b> Screenshots from bank accounts or payment methods cannot be used, as
                    they do not display the <b>item name</b> or <b>order number.</b></li>
                </ul>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-textarea
              v-model="formDetails.description"
              :rules="[v => !!v]"
              :label=bugReport.strings.BUG_DESCRIPTION
              :placeholder=bugReport.strings.BUG_INFO
              required variant="solo-filled"
          ></v-textarea>
          <!-- Extra Info -->
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-title class="text-subtitle-1 bg-amber-lighten-4">
                {{ bugReport.strings.BUG_EXTRA }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-text-field
                    v-model="formDetails.os"
                    :label="bugReport.strings.BUG_OPERATING"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.osversion"
                    :label="bugReport.strings.BUG_OS_VERSION"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.device"
                    :label="bugReport.strings.BUG_DEVICE_MODEL"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.locale"
                    :label="bugReport.strings.BUG_LOCALE"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.appname"
                    :label="bugReport.strings.BUG_APP_NAME"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.bundleversion"
                    :label="bugReport.strings.BUG_APP_VERSION"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.currentid"
                    :label="bugReport.strings.BUG_ID"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.memused"
                    :label="bugReport.strings.BUG_MEM_USED"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.memfree"
                    :label="bugReport.strings.BUG_MEM_FREE"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.totalmemused"
                    :label="bugReport.strings.BUG_TOTAL_MEM"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.uptime"
                    :label="bugReport.strings.BUG_UPTIME"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.steamid"
                    :label="bugReport.strings.BUG_STEAM"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.cpu"
                    :label="bugReport.strings.BUG_CPU"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.gpu"
                    :label="bugReport.strings.BUG_GPU"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.graphicsdriver"
                    :label="bugReport.strings.BUG_GRAPHICS"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.systemmem"
                    :label="bugReport.strings.BUG_SYSMEM"
                    readonly variant="solo-filled">
                </v-text-field>
                <v-text-field
                    v-model="formDetails.graphicsmem"
                    :label="bugReport.strings.BUG_GPUMEM"
                    readonly variant="solo-filled">
                </v-text-field>
                <p class="text-red" v-if="noShareWarning">
                  {{ bugReport.strings.BUG_NOSHARE }}
                </p>
                <v-btn
                    color="yellow-darken-3"
                    class="mt-4 text-white small-button-height"
                    block
                    @click="switchData"
                >
                  <span style="white-space: normal">
                    {{ shareExtraInfo ? bugReport.strings.BUG_NO_SHARE : bugReport.strings.BUG_SHARE }}
                  </span>
                </v-btn>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
          <!-- Google Captcha -->
          <GoogleRecaptcha ref="recaptcha" @verify="onSubmit"></GoogleRecaptcha>
          <!-- Submit -->
          <v-btn
              color="yellow-darken-3"
              class="mt-4 mb-5 text-white"
              block
              @click="executeRecaptcha"
          >
            {{ bugReport.strings.BUG_SUBMIT }}
          </v-btn>
        </v-form>
      </v-col>
    </v-row>
    <!-- Success or Error response after submit -->
    <v-row v-else class="mt-10">
      <v-col offset-lg="2" lg="8" offset="1" cols="10">
        <p v-if="error">{{ bugReport.strings.PRIVACY_ERROR }}</p>
        <p v-else v-html="bugReport.strings.BUG_THANKS_MESSAGE"></p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {mapActions, mapState} from "vuex";
import GoogleRecaptcha from "@/components/Recaptcha";

export default {
  name: "AccountAndData",
  components: {GoogleRecaptcha},
  data() {
    return {
      formDetails: {},
      loading: false,
      shareExtraInfo: true,
      noShareWarning: false,
      formSubmit: false,
      error: false,
      copyFormDetails: {},
      appList: []
    }
  },
  computed: {
    ...
        mapState({
          bugReport: state => state.supportStore.bugReport,
        }),
  },
  mounted() {
    this.getBugReport({
      params: {
        ...this.$route.query
      }
    }).then((response) => {
      this.loading = true
      this.formDetails = JSON.parse(JSON.stringify(response.data.form_details))
      response.data.appList.forEach(app => {
        this.appList.push({
          value: app[0],
          title: app[1],
        })
        // Contact us form - Set game name
        app[0].toLowerCase() == this.formDetails.game.toLowerCase()
            ? this.formDetails.game = app[0]
            : this.formDetails.game === '' && app[0].toLowerCase() == this.formDetails.appname.toLowerCase()
                ? this.formDetails.game = app[0] : null
      })
    })
  },
  methods: {
    switchData() {
      if (this.shareExtraInfo) {
        this.copyFormDetails = JSON.parse(JSON.stringify(this.formDetails))
        let newFormData = {
          os: '',
          osversion: '',
          device: '',
          locale: '',
          appname: '',
          bundleversion: '',
          currentid: '',
          memused: '',
          memfree: '',
          totalmemused: '',
          uptime: '',
          steamid: '',
          cup: '',
          gpu: '',
          graphicsmem: '',
          systemmem: '',
          graphicsdriver: ''
        }
        this.formDetails = {...this.formDetails, ...newFormData}
        this.noShareWarning = true
      } else {
        this.formDetails = {
          ...this.copyFormDetails,
          name: this.formDetails.name,
          email: this.formDetails.email,
          subject: this.formDetails.subject,
          game: this.formDetails.game,
          platform: this.formDetails.platform,
          description: this.formDetails.description
        }
        this.noShareWarning = false
      }
      this.shareExtraInfo = !this.shareExtraInfo
    },

    async onSubmit(result) {
      const {valid} = await this.$refs.form.validate()
      if (valid) {
        this.postBugReport({
          data: {
            ...this.formDetails,
            grecaptchaResponse: result,
            formType: 'lost_items_and_account_recovery'
          }
        }).then((response) => {
          this.formSubmit = true
          this.error = response.data.error
        })
      } else {
        // Scroll to first error field
        this.$nextTick(() => {
          let topOfElement = document.querySelector('.v-field--error.v-field--error:first-of-type').offsetTop - 100
          window.scroll({top: topOfElement, behavior: "smooth"})
        })
      }
    },

    executeRecaptcha() {
      this.$refs.recaptcha.execute()
    },

    validateUsername() {
      let blocked = ['n/a', 'na']
      let val = (this.formDetails.in_game_username || '').trim().toLowerCase()
      if (blocked.includes(val)) {
          this.formDetails.in_game_username = ''
      }
    },

    ...mapActions([
      'getBugReport',
      'postBugReport'
    ])
  }
}
</script>

<style scoped lang="scss">
@media only screen and (max-width: 550px) {
  .small-button-height {
    height: 70px;
  }
}
</style>
